// This component owns only local UI state: search input, filter popout visibility, and popout
// filter values. All Redux access and router state live in useMiscMyRecords, consumed here as `mr`.
// Tests mock the hook rather than the Redux store or router.
import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import SearchIcon from '@tecuity/sos-icons/react/search-heavy'
import CrossIcon from '@tecuity/sos-icons/react/cross-heavy'
import FilterIcon from '@tecuity/sos-icons/react/filter'
import { Body } from '../Shell/Shell'
import { Spinner } from '../Loaders/Loaders'
import EmptyBox from '../MyRecords/EmptyBox'
import { DumbField } from 'components/Wizard2/Field'
import { TextInput, Select } from 'components/Wizard2/fieldComponents/Form'
import DateRange from 'components/Wizard2/fieldComponents/DateRange'
import MiscRecordsModuleDropdown from './MiscRecordsModuleDropdown'
import MiscellaneousRecordTable from './MiscellaneousRecordTable'
import MiscellaneousRecordDrawer from './MiscellaneousRecordDrawer'
import { useMiscMyRecords } from './useMiscMyRecords'
import './MiscellaneousMyRecords.scss'

const FilterChip = ({ label, value, name, onRemove }) => (
  <span className="filter-chip">
    <div className="label">{label}</div>
    <div className="value">{value}</div>
    <button
      type="button"
      aria-label={'Remove ' + label + ' filter'}
      onClick={() => { onRemove(name) }}
    >
      <div className="center">
        <CrossIcon />
      </div>
    </button>
  </span>
)

const emptyPopoutFilters = {
  registrationDateStart: null,
  registrationDateEnd: null,
  advancedFilters: {},
}

const MiscellaneousMyRecords = ({ sublinks }) => {
  const mr = useMiscMyRecords({ sublinks })

  const [searchInput, setSearchInput] = useState('')
  const [filterOpen, setFilterOpen] = useState(false)
  const [popoutFilters, setPopoutFilters] = useState(emptyPopoutFilters)

  useEffect(() => {
    setSearchInput('')
    setFilterOpen(false)
    setPopoutFilters(emptyPopoutFilters)
  }, [mr.selectedModuleId])

  const handleSearch = () => {
    mr.search(searchInput)
  }

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleClearFilters = () => {
    setSearchInput('')
    setFilterOpen(false)
    setPopoutFilters(emptyPopoutFilters)
    mr.clearFilters()
  }

  const applyPopoutFilters = () => {
    mr.applyFilters(popoutFilters)
    setFilterOpen(false)
  }

  const handleToggleFilter = () => {
    if (!filterOpen) {
      setPopoutFilters({
        registrationDateStart: (mr.filters && mr.filters.registrationDateStart) || null,
        registrationDateEnd: (mr.filters && mr.filters.registrationDateEnd) || null,
        advancedFilters: Object.assign({}, (mr.filters && mr.filters.advancedFilters) || {}),
      })
      setFilterOpen(true)
    } else {
      applyPopoutFilters()
    }
  }

  const handleRemoveChip = (name) => {
    if (name === 'search') {
      setSearchInput('')
    }
    mr.removeChip(name)
  }

  const renderAdvancedField = (field) => {
    const dataType = field.DataType
    const fieldName = field.FieldName
    const advancedFilters = popoutFilters.advancedFilters

    if (dataType === 'String') {
      return (
        <DumbField key={fieldName} label={field.DisplayName} fieldId={fieldName} type="text">
          <TextInput
            fieldId={fieldName}
            value={advancedFilters[fieldName] || ''}
            onChange={(val) => {
              setPopoutFilters((prev) => ({ ...prev, advancedFilters: { ...prev.advancedFilters, [fieldName]: val } }))
            }}
            onBlur={() => {}}
            width="100%"
          />
        </DumbField>
      )
    }

    if (dataType === 'Name' || dataType === 'Address' || dataType === 'PhoneNumber') {
      return (
        <DumbField key={fieldName} label={field.DisplayName} fieldId={fieldName} type="text">
          <TextInput
            fieldId={fieldName}
            value={advancedFilters[fieldName] || ''}
            onChange={(val) => {
              setPopoutFilters((prev) => ({ ...prev, advancedFilters: { ...prev.advancedFilters, [fieldName]: val } }))
            }}
            onBlur={() => {}}
            width="100%"
          />
        </DumbField>
      )
    }

    if (dataType === 'Bool') {
      const boolOptions = [
        { value: '', label: 'Any' },
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ]
      return (
        <DumbField key={fieldName} label={field.DisplayName} fieldId={fieldName} type="select">
          <Select
            fieldId={fieldName}
            options={boolOptions}
            value={advancedFilters[fieldName] || ''}
            onChange={(val) => {
              setPopoutFilters((prev) => ({ ...prev, advancedFilters: { ...prev.advancedFilters, [fieldName]: val || '' } }))
            }}
            onBlur={() => {}}
            width="100%"
          />
        </DumbField>
      )
    }

    if (dataType === 'Date' || dataType === 'DateTime') {
      return (
        <DumbField key={fieldName} label={field.DisplayName} fieldId={fieldName} type="DateRange" ownsLabel>
          <div className="date-range-wrapper">
            <DateRange
              fieldId={fieldName}
              label={field.DisplayName}
              startLabel="Start"
              endLabel="End"
              ownsLabel
              stacked
              value={{
                start: advancedFilters[fieldName + 'Start'] || '',
                end: advancedFilters[fieldName + 'End'] || '',
              }}
              onChange={(val) => {
                setPopoutFilters((prev) => ({
                  ...prev,
                  advancedFilters: {
                    ...prev.advancedFilters,
                    [fieldName + 'Start']: (val && val.start) || null,
                    [fieldName + 'End']: (val && val.end) || null,
                  },
                }))
              }}
              onBlur={() => {}}
            />
          </div>
        </DumbField>
      )
    }

    if (dataType === 'LookupType') {
      let lookupOptions = []
      try {
        lookupOptions = JSON.parse(field.LookupTypeValuesJson || '[]')
      } catch (e) {
        lookupOptions = []
      }
      if (!Array.isArray(lookupOptions)) {
        lookupOptions = []
      }
      const selectOptions = [{ value: '', label: 'Any' }].concat(
        lookupOptions.map((item) => ({ value: String(item.value), label: item.label }))
      )
      return (
        <DumbField key={fieldName} label={field.DisplayName} fieldId={fieldName} type="select">
          <Select
            fieldId={fieldName}
            options={selectOptions}
            value={advancedFilters[fieldName] || ''}
            onChange={(val) => {
              setPopoutFilters((prev) => ({ ...prev, advancedFilters: { ...prev.advancedFilters, [fieldName]: val || '' } }))
            }}
            onBlur={() => {}}
            width="100%"
          />
        </DumbField>
      )
    }

    return null
  }

  const hasAdvancedFilterFields = useMemo(
    () => !!(mr.template && mr.template.SearchableFields && mr.template.SearchableFields.length > 0),
    [mr.template],
  )

  return (
    <Body showDrawer={!!mr.selectedRecordId}>
      <div className="misc-my-records">
        <div className="table-controls-wrapper">
          <div className="toolbar">
            <div className="misc-my-records-module">
              <div className="misc-my-records-module-inner">
                <MiscRecordsModuleDropdown modules={mr.modules} sourceTypeParam={mr.sourceTypeParam} showLabel={true} />
              </div>
            </div>

            {mr.templateLoading ? (
              <div className="reports-spinner animated fadeIn">
                <Spinner size="medium" themeColor="secondary" />
              </div>
            ) : (
              <div className="misc-my-records-toolbar-row">
                {mr.selectedModule && (
                  <h2 className="misc-my-records-heading">
                    {'My ' + mr.selectedModule.DisplayName + ' Records'}
                  </h2>
                )}

                <div className="filter-wrapper">
                  <input
                    type="text"
                    className="filter"
                    placeholder="Search My Records"
                    value={searchInput}
                    onChange={(e) => { setSearchInput(e.target.value) }}
                    onKeyDown={handleSearchKeyDown}
                    aria-label="Search"
                  />
                  <button
                    type="button"
                    className="search btn btn-primary btn-raised"
                    aria-label="Search"
                    onClick={handleSearch}
                  >
                    <SearchIcon />
                  </button>
                  {hasAdvancedFilterFields && (
                    <button
                      type="button"
                      className="search btn btn-primary btn-raised"
                      aria-label="Filter"
                      onClick={handleToggleFilter}
                    >
                      <FilterIcon className="filter-icon" />
                    </button>
                  )}
                  {mr.filterChips.length > 0 && (
                    <button
                      type="button"
                      className="search btn btn-primary btn-raised"
                      aria-label="Clear Filters"
                      onClick={handleClearFilters}
                    >
                      <CrossIcon alt="Clear filters" aria-hidden />
                    </button>
                  )}
                  {filterOpen && (
                    <>
                      <div className="filter-popout wizard">
                        <h3>Filters</h3>
                        <DumbField label="Registration Date" fieldId="registrationDate" type="DateRange" ownsLabel>
                          <div className="date-range-wrapper">
                            <DateRange
                              fieldId="registrationDate"
                              label="Registration Date"
                              startLabel="Start"
                              endLabel="End"
                              ownsLabel
                              stacked
                              value={{
                                start: popoutFilters.registrationDateStart || '',
                                end: popoutFilters.registrationDateEnd || '',
                              }}
                              onChange={(val) => {
                                setPopoutFilters((prev) => ({
                                  ...prev,
                                  registrationDateStart: (val && val.start) || null,
                                  registrationDateEnd: (val && val.end) || null,
                                }))
                              }}
                              onBlur={() => {}}
                            />
                          </div>
                        </DumbField>

                        {mr.template && (mr.template.SearchableFields || []).map(renderAdvancedField)}
                      </div>
                      <div
                        className="filter-popout-shade animated fadeIn extra-fast-animation"
                        onClick={applyPopoutFilters}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {mr.filterChips.length > 0 && (
            <div className="filter-chips misc-my-records-filter-chips">
              {mr.filterChips.map((chip) => (
                <FilterChip
                  key={chip.name}
                  label={chip.label}
                  value={chip.value}
                  name={chip.name}
                  onRemove={handleRemoveChip}
                />
              ))}
            </div>
          )}
        </div>

        {!mr.templateLoading && mr.loading && (
          <div className="misc-my-records-loading">
            <Spinner size="medium" themeColor="secondary" />
          </div>
        )}

        {!mr.templateLoading && !mr.loading && !mr.error && mr.selectedModule && (!mr.items || mr.items.length === 0) && (
          <div className="empty-placeholder-wrapper">
            <EmptyBox />
            <h2>{"Looks like you don't have any " + mr.selectedModule.DisplayName + ' records yet'}</h2>
            <p>{'Visit ' + mr.selectedModule.DisplayName + ' forms to create your first record'}</p>
            <div className="flex-row center">
              <Link className="btn btn-primary btn-raised" to={'/forms/' + mr.sourceTypeParam}>
                {'Go to ' + mr.selectedModule.DisplayName + ' forms'}
              </Link>
            </div>
          </div>
        )}

        {!mr.templateLoading && !mr.loading && (
          <MiscellaneousRecordTable
            columns={mr.template && mr.template.Columns}
            items={mr.items}
            totalCount={mr.totalCount}
            loading={false}
            error={mr.error}
            selectedRecordId={mr.selectedRecordId}
            onRowClick={mr.handleRowClick}
            onViewFiling={mr.handleViewFiling}
          />
        )}
      </div>

      <MiscellaneousRecordDrawer
        show={!!mr.selectedRecordId}
        record={mr.selectedRecord}
        columns={mr.template && mr.template.Columns}
        onDismiss={mr.dismissDrawer}
        drawerData={mr.drawerData}
        drawerLoading={!!mr.drawerLoading}
      />
    </Body>
  )
}

export default MiscellaneousMyRecords
